'use client'
import { type FC, useMemo, useEffect, useRef, useCallback } from 'react'
import { Box, Button, Divider, Flex, HStack, SimpleGrid, Stack, VStack } from '@chakra-ui/react'
import { MdProductionQuantityLimits } from 'react-icons/md'
import { AiFillFilter } from 'react-icons/ai'
import { ActivityLoader, EmptyMessage, GrandSection } from 'ui'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { useOnScreen } from 'ui/hooks'
import { overrideText } from 'ui/lib/overrides'
import type { ProductLightDTO } from 'ecosystem'
import ProductListItemComponent, { type ProductListItemProps } from '../../ProductListItem'
import { type ProductListSearchProps } from '../types'
import { ProductListSearchHorizontalFilters } from './ProductListSearchHorizontalFilters'

export type ProductListSearchHorizontalProps = Omit<ProductListSearchProps, 'variant'>

export interface ProductListSearchOverrides {
  sort?: string
  productListSearchTitle?: string
  viewTotalProducts?: string
  noProductsFound?: string
}

export const ProductListSearchHorizontal: FC<ProductListSearchHorizontalProps> = ({
  isLoading,
  title,
  items,
  totalItems,
  deliveryOptions,
  infiniteScrollConfig,
  searchProductsState,
  onMore,
  onAddToCart,
  cartItems,
  onProductRequest,
  productsUrlPath,
  customComponents,
  filterProps,
  columnsNumber: _columnsNumber,
  columnsNumberTablet: _columnsNumberTablet,
  columnsNumberMobile: _columnsNumberMobile,
  imageAspectRatio: _imageAspectRatio,
  imageAspectRatioTablet,
  imageAspectRatioMobile,
  ...props
}) => {
  const overrides = useOverridesContext<keyof ProductListSearchOverrides>()

  const columnsNumber = (_columnsNumber ?? 0) > 0 ? _columnsNumber! : 4
  const columnsNumberTablet =
    (_columnsNumberTablet ?? 0) > 0 ? _columnsNumberTablet! : columnsNumber
  const columnsNumberMobile = (_columnsNumberMobile ?? 0) > 0 ? _columnsNumberMobile! : 2

  const imageAspectRatio = useMemo<ProductListItemProps['imageAspectRatio']>(() => {
    if (!_imageAspectRatio && !imageAspectRatioTablet && !imageAspectRatioMobile) {
      return undefined
    }

    if (imageAspectRatioMobile || imageAspectRatioTablet) {
      return {
        ...(imageAspectRatioMobile || _imageAspectRatio
          ? { base: imageAspectRatioMobile || _imageAspectRatio }
          : {}),
        ...(imageAspectRatioTablet || _imageAspectRatio
          ? { sm: imageAspectRatioTablet || _imageAspectRatio }
          : {}),
        ...(_imageAspectRatio ? { md: _imageAspectRatio } : {})
      }
    }

    return _imageAspectRatio
  }, [_imageAspectRatio, imageAspectRatioMobile, imageAspectRatioTablet])

  // ******** INFINITE SCROLL ********

  const loadMoreIntersectionDiv = useRef<HTMLDivElement | null>(null)
  const isLastElementReached = useOnScreen(loadMoreIntersectionDiv)
  const { shouldLoadMore, loadMore } = infiniteScrollConfig

  useEffect(() => {
    if (isLastElementReached && shouldLoadMore && !isLoading) {
      loadMore()
    }
  }, [isLastElementReached, isLoading, shouldLoadMore, loadMore])

  const onBuy = useCallback(
    (product: ProductLightDTO) => {
      const itemInCart = cartItems.find((item) => item.id === product.id)

      const qty = (itemInCart?.qty ?? 0) + 1

      onAddToCart({
        id: product.id,
        type: 'SINGLE',
        qty,
        meta: {
          singleType: {
            product
          }
        }
      })
    },
    [cartItems, onAddToCart]
  )

  const { ProductListItem = ProductListItemComponent } = customComponents || {}

  return (
    <>
      <GrandSection
        contentContainerProps={{
          spacing: 4
        }}
        {...{ title }}
        {...props}
      >
        <Stack
          align={{
            base: 'flex-start',
            lg: 'center'
          }}
          direction={{ base: 'column', lg: 'row' }}
          justify="flex-start"
          spacing={[2, 4]}
        >
          <ProductListSearchHorizontalFilters
            display={{ base: 'none', md: 'flex' }}
            flex={1}
            {...{
              ...filterProps,
              searchProductsState,
              onMore
            }}
          />
          <Box
            color="text.mild"
            fontSize="sm"
            fontWeight={600}
            pb={2}
            textAlign={{ base: 'right', md: 'left' }}
          >
            {overrideText('visar produkter', overrides?.viewTotalProducts)} {totalItems}
          </Box>
        </Stack>

        {/* Mobile filter button */}
        <VStack align="flex-start" display={{ base: 'block', md: 'none' }} w="full">
          <Button
            aria-label="show-filters"
            bg="background.mildDark"
            borderColor="background.mildDark"
            borderRadius="md"
            minH="35px"
            minW={100}
            onClick={onMore}
            size="sm"
            w={{ base: 'full', sm: 'auto' }}
          >
            <HStack justify="center" w="full">
              <AiFillFilter />
              <Box>{overrideText('Sortera och filtrera', overrides?.productListSearchTitle)}</Box>
            </HStack>
          </Button>
        </VStack>

        <Divider />

        <Box minH={500} position="relative" w="full">
          {!!items.length && (
            <VStack w="full">
              <SimpleGrid
                columns={{
                  base: columnsNumberMobile,
                  sm: columnsNumberTablet,
                  md: columnsNumber
                }}
                gap={2}
                spacing={{
                  base: 1,
                  md: 6
                }}
                width="100%"
              >
                {items.map((item) => (
                  <ProductListItem
                    showsPrice
                    key={item.id}
                    onBuy={() => {
                      onBuy(item)
                    }}
                    imageProps={{
                      sizes: `(max-width: 40em) ${100 / columnsNumberMobile}vw, (max-width: 52em) ${100 / columnsNumber}vw, (max-width: 52em) ${100 / columnsNumber}vw, 290px`
                    }}
                    imageAspectRatio={imageAspectRatio}
                    {...{ item, deliveryOptions, onProductRequest, productsUrlPath }}
                  />
                ))}
              </SimpleGrid>

              {!!isLoading && (
                <ActivityLoader
                  fontSize="xs"
                  h="full"
                  position="relative"
                  spinnerProps={{ size: 'sm' }}
                  text=""
                  w="full"
                  zIndex={0}
                />
              )}
            </VStack>
          )}

          {isLoading && !items.length ? (
            <ActivityLoader
              fontSize="xs"
              h="full"
              position="relative"
              spinnerProps={{ size: 'sm' }}
              text=""
              w="full"
              zIndex={0}
            />
          ) : null}

          {!items.length && !isLoading && (
            <Flex align="center" h={500} justify="center">
              <EmptyMessage
                fontSize="xl"
                icon={<MdProductionQuantityLimits />}
                text={overrideText('Inga produkter hittades', overrides?.noProductsFound)}
              />
            </Flex>
          )}
        </Box>
      </GrandSection>

      <Box h="1px" ref={loadMoreIntersectionDiv} w="full" />
    </>
  )
}
